import axios from "axios"

const baseURL = process.env.REACT_APP_API_URL
const refreshURL = `${baseURL}auth/refresh`

// ── Helper: limpiar sesión y redirigir al login ──
const forceLogout = () => {
  localStorage.removeItem("authUser")
  localStorage.removeItem("empresaSeleccionada")
  localStorage.removeItem("usuario")
  if (!window.location.pathname.includes("/login")) {
    window.location.href = "/login"
  }
}

/**
 * Crea una instancia de axios con:
 *  - baseURL configurable
 *  - inyección automática de Bearer token desde localStorage
 *  - refresh automático del accessToken en 401 (con queue para concurrencia)
 *  - logout automático en 403 o si el refresh falla
 *
 * El refresh siempre se hace contra `REACT_APP_API_URL` (un solo backend de auth),
 * sin importar el baseURL del cliente.
 */
export function createAuthenticatedAxios(instanceBaseURL) {
  const client = axios.create({
    baseURL: instanceBaseURL,
    headers: { "Content-Type": "application/json" },
  })

  // Request interceptor: inyecta accessToken
  client.interceptors.request.use(
    config => {
      try {
        const authData = JSON.parse(localStorage.getItem("authUser"))
        if (authData?.accessToken) {
          config.headers.Authorization = `Bearer ${authData.accessToken}`
        }
      } catch {
        // localStorage corrupto → no agrega header
      }
      return config
    },
    error => Promise.reject(error),
  )

  // Estado del refresh compartido por instancia
  let isRefreshing = false
  let failedQueue = []

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) prom.reject(error)
      else prom.resolve(token)
    })
    failedQueue = []
  }

  // Response interceptor: refresh automático
  client.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config
      const status = error.response?.status

      if (
        (status !== 401 && status !== 403) ||
        originalRequest._retry ||
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/login")
      ) {
        return Promise.reject(error)
      }

      if (status === 403) {
        forceLogout()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return client(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const authData = JSON.parse(localStorage.getItem("authUser"))
        if (!authData?.refreshToken) {
          throw new Error("No refresh token available")
        }

        const refreshResponse = await axios.post(refreshURL, {
          refreshToken: authData.refreshToken,
        })

        const newAccessToken =
          refreshResponse.data?.data?.accessToken ||
          refreshResponse.data?.accessToken

        if (!newAccessToken) {
          throw new Error("No access token in refresh response")
        }

        authData.accessToken = newAccessToken
        localStorage.setItem("authUser", JSON.stringify(authData))

        processQueue(null, newAccessToken)

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return client(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        forceLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    },
  )

  return client
}

const axiosClient = createAuthenticatedAxios(baseURL)

export default axiosClient
